import { API_BASE } from '../config'

const ONBOARD = `${API_BASE}/onboard`

// ── Community ────────────────────────────────────────────────────────────────

export async function registerCommunity({ communityName, address, adminFirstName, adminLastName, adminEmail, adminPhone, userType, unitNbr }, idToken) {
  const resp = await fetch(`${ONBOARD}/community`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityName, address, adminFirstName, adminLastName, adminEmail, adminPhone, userType, unitNbr }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to register community')
  return data
}

export async function verifyOTP({ communityId, email, otp }, idToken) {
  const resp = await fetch(`${ONBOARD}/community/verify-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, email, otp }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'OTP verification failed')
  return data
}

export async function getCommunity({ communityId, adminEmail } = {}) {
  const params = new URLSearchParams()
  if (communityId) params.set('communityId', communityId)
  if (adminEmail) params.set('adminEmail', adminEmail)
  const idToken = sessionStorage.getItem('id_token')
  const resp = await fetch(`${ONBOARD}/community?${params.toString()}`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to fetch community')
  return data
}

export async function updateCommunityGates({ communityId, gates }, idToken) {
  const resp = await fetch(`${ONBOARD}/community/gates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, gates }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to update gates')
  return data
}

// ── Resident Invites ─────────────────────────────────────────────────────────

export async function createResidentInvite({ communityId, email, firstName, lastName, unitNbr, mobileNbr, residentType, vehicleType, vehicleNbr, vehicleMake, vehicleModel, vehicleYear }, idToken) {
  const resp = await fetch(`${ONBOARD}/resident-invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, email, firstName, lastName, unitNbr, mobileNbr, residentType, vehicleType, vehicleNbr, vehicleMake, vehicleModel, vehicleYear }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to create invite')
  return data
}

export async function bulkResidentInvite({ communityId, residents }, idToken) {
  const resp = await fetch(`${ONBOARD}/resident-invite/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, residents }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Bulk invite failed')
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

export async function listResidentInvites(communityId, idToken) {
  const resp = await fetch(`${ONBOARD}/resident-invite?communityId=${encodeURIComponent(communityId)}`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to list invites')
  return data
}

export async function getResidentInviteByToken(token) {
  const resp = await fetch(`${ONBOARD}/resident-invite?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Invalid or expired invite link')
  return data
}

export async function searchOwners({ communityId, firstName, lastName, query } = {}, idToken) {
  const params = new URLSearchParams()
  if (communityId) params.set('communityId', communityId)
  if (firstName) params.set('firstName', firstName)
  if (lastName) params.set('lastName', lastName)
  if (query) params.set('query', query)

  const resp = await fetch(`${ONBOARD}/owners/search?${params.toString()}`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Owner search failed')
  return Array.isArray(data) ? data : []
}

// ── Resident Registration ────────────────────────────────────────────────────

export async function registerResident({ token, email, firstName, lastName }) {
  const resp = await fetch(`${ONBOARD}/resident-register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, email, firstName, lastName }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Registration failed')
  return data
}

// ── CSV Parsing Helper ───────────────────────────────────────────────────────

/**
 * Parses a CSV file (File object) into an array of resident objects.
 * Expected headers (case-insensitive):
 * first_name / firstName, email, mobile_nbr / mobile / phone,
 * resident_type, vehicle_type, vehicle_nbr, vehicle_make, vehicle_model, vehicle_year
 * Optional headers: last_name / lastName, unit_nbr / unit
 * Returns: [{ firstName, lastName, email, unitNbr, mobileNbr, residentType, vehicleType, vehicleNbr, vehicleMake, vehicleModel, vehicleYear }]
 */
export function parseResidentCSV(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target.result
        const lines = text.split(/\r?\n/).filter(l => l.trim())
        if (lines.length < 2) {
          reject(new Error('CSV must have a header row and at least one data row'))
          return
        }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''))
        const getIdx = (...names) => {
          for (const n of names) {
            const idx = headers.indexOf(n)
            if (idx !== -1) return idx
          }
          return -1
        }
        const firstIdx = getIdx('first_name', 'firstname', 'first name')
        const lastIdx = getIdx('last_name', 'lastname', 'last name')
        const emailIdx = getIdx('email')
        const mobileIdx = getIdx('mobile_nbr', 'mobilenbr', 'mobile number', 'mobile', 'phone', 'phone_nbr', 'phone number')
        const unitIdx = getIdx('unit_nbr', 'unit', 'unitnbr', 'unit number')
        const residentTypeIdx = getIdx('resident_type', 'residenttype', 'resident type')
        const vehicleTypeIdx = getIdx('vehicle_type', 'vehicletype', 'vehicle type')
        const vehicleNbrIdx = getIdx('vehicle_nbr', 'vehiclenbr', 'vehicle_nbr', 'vehicle number', 'vehicle_number', 'vehicle no')
        const vehicleMakeIdx = getIdx('vehicle_make', 'vehiclemake', 'vehicle make')
        const vehicleModelIdx = getIdx('vehicle_model', 'vehiclemodel', 'vehicle model')
        const vehicleYearIdx = getIdx('vehicle_year', 'vehicleyear', 'vehicle year', 'year_of_make', 'year of make')

        if (firstIdx === -1 || emailIdx === -1 || mobileIdx === -1 || residentTypeIdx === -1 || vehicleTypeIdx === -1 || vehicleNbrIdx === -1 || vehicleMakeIdx === -1 || vehicleModelIdx === -1 || vehicleYearIdx === -1) {
          reject(new Error('CSV must have "first_name", "email", "mobile_nbr", "resident_type", "vehicle_type", "vehicle_nbr", "vehicle_make", "vehicle_model", and "vehicle_year" columns'))
          return
        }

        const residents = []
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''))
          const firstName = cols[firstIdx] || ''
          const lastName = lastIdx !== -1 ? (cols[lastIdx] || '') : ''
          const email = (cols[emailIdx] || '').toLowerCase()
          const mobileNbr = mobileIdx !== -1 ? (cols[mobileIdx] || '') : ''
          const unitNbr = unitIdx !== -1 ? (cols[unitIdx] || '') : ''
          const residentType = residentTypeIdx !== -1 ? (cols[residentTypeIdx] || '') : ''
          const vehicleType = vehicleTypeIdx !== -1 ? (cols[vehicleTypeIdx] || '') : ''
          const vehicleNbr = vehicleNbrIdx !== -1 ? (cols[vehicleNbrIdx] || '') : ''
          const vehicleMake = vehicleMakeIdx !== -1 ? (cols[vehicleMakeIdx] || '') : ''
          const vehicleModel = vehicleModelIdx !== -1 ? (cols[vehicleModelIdx] || '') : ''
          const vehicleYear = vehicleYearIdx !== -1 ? (cols[vehicleYearIdx] || '') : ''
          if (!firstName || !email || !mobileNbr || !residentType || !vehicleType || !vehicleNbr || !vehicleMake || !vehicleModel || !vehicleYear) continue
          residents.push({ firstName, lastName, email, unitNbr, mobileNbr, residentType, vehicleType, vehicleNbr, vehicleMake, vehicleModel, vehicleYear })
        }
        resolve(residents)
      } catch (err) {
        reject(new Error('Failed to parse CSV: ' + err.message))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

// ── AppAdmin ─────────────────────────────────────────────────────────────────

export async function listPendingCommunities(idToken) {
  const resp = await fetch(`${ONBOARD}/community/pending`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to list pending communities')
  return Array.isArray(data) ? data : []
}

export async function approveCommunity({ communityId, action, reason, trialMonths }, idToken) {
  const resp = await fetch(`${ONBOARD}/community/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, action, reason, trialMonths }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Action failed')
  return data
}

export async function listApprovedCommunities(idToken) {
  const resp = await fetch(`${ONBOARD}/community/approved`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to list approved communities')
  return Array.isArray(data) ? data : []
}

// ── Plan Upgrade Requests ────────────────────────────────────────────────────

export async function submitUpgradeRequest({ communityId, communityName, feature, label }, idToken) {
  const resp = await fetch(`${ONBOARD}/upgrade-request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ communityId, communityName, feature, label }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to submit upgrade request')
  return data
}

export async function listUpgradeRequests(status, idToken) {
  const resp = await fetch(`${ONBOARD}/upgrade-request?status=${encodeURIComponent(status)}`, {
    method: 'GET',
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Failed to list upgrade requests')
  return Array.isArray(data) ? data : []
}

export async function approveUpgradeRequest({ requestId, communityId, action, reason }, idToken) {
  const resp = await fetch(`${ONBOARD}/upgrade-request/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ requestId, communityId, action, reason }),
  })
  const data = await resp.json()
  if (!resp.ok) throw new Error(data.error || 'Action failed')
  return data
}
